import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { getAllCommunities } from 'apis/CommunityAPI';
import Dropdown from 'components/lib/Dropdown';
import Input from 'components/lib/Input';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { CommunityAdminListType } from 'types/communities';
import { useDebounce } from 'utils/hooks/useDebounce';

import CommunityAdminContentCompetitorsList from './CommunityAdminContentCompetitorsList';
import { useCommunityAdminContext } from './CommunityAdminContextProvider';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 20px;
  gap: 10px;
  height: 100%;
  overflow: hidden;
`;

const StyledDropdownMenu = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line-gray);
  justify-content: center;
  align-items: center;
  background-color: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

const MenuWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 22px;
    max-height: 160px;
    width: 100%;
    
    .ant-dropdown-menu {
        padding: 0;
        border-radius: 0;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
        overflow-y: auto;

        .ant-dropdown-menu-item {
            font-size: 12px;
            padding: 0px;
            border-radius: 0;
            border-bottom: 1px solid var(--line-gray);
        }
`;

const CommunityItem = styled.div`
  display: flex;
  gap: 4px;
  padding: 5px 12px;
  .light {
    color: var(--text-secondary);
  }
`;

const CommunityAdminContentCompetitorsSearch = () => {
  const { communityId, competitors, setCompetitors } = useCommunityAdminContext();

  const [search, setSearch] = useState('');
  const [competitorsOptions, setCompetitorsOptions] = React.useState<any[]>([]);
  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['communities', debouncedSearch],
    queryFn: () =>
      getAllCommunities({
        page: 1,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
        companies_relation: COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY,
        search: debouncedSearch,
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const options = (data?.results || [])
      .filter(
        (community: CommunityAdminListType) =>
          !competitors.some((item: CommunityAdminListType) => item.id == community.id) && community.id !== communityId
      )
      .map((community: CommunityAdminListType) => ({
        value: community.id,
        label: (
          <CommunityItem onClick={() => handleSelectCompetitor(community)}>
            <span>{community.comunity_name}</span>
            <span className={'light'}>·</span>
            <span className={'light'}>{community.company.name}</span>
          </CommunityItem>
        ),
      }));
    setCompetitorsOptions(options);
  }, [data, competitors]);

  const handleSelectCompetitor = (value: any) => {
    setCompetitors((prev: any) => [...prev, value]);
  };

  return (
    <Container>
      <Dropdown
        menu={{ items: competitorsOptions }}
        trigger={['click']}
        dropdownRender={(menu: any) => (
          <StyledDropdownMenu className={'mini-scroll'}>
            {(isLoading || isFetching) && <LoaderRibbed expanded size={'mini'} />}
            {!isLoading && !isFetching && competitorsOptions.length === 0 && <span>No results</span>}
            {!isLoading && !isFetching && competitorsOptions.length > 0 && (
              <MenuWrapper className={'mini-scroll'}>{menu}</MenuWrapper>
            )}
          </StyledDropdownMenu>
        )}
      >
        <Input
          prefix={<SearchOutlined />}
          onChange={(event: any) => setSearch(event.target.value)}
          placeholder="Search competitors to add"
        />
      </Dropdown>
      <CommunityAdminContentCompetitorsList />
    </Container>
  );
};

export default CommunityAdminContentCompetitorsSearch;
